import {Button, ButtonGroup, Card, IconButton, Modal, Select, Spinner, TextField, Tooltip} from "@nike/eds";
import {Check, Close, Question, Search} from "@nike/nike-design-system-icons";
import type {ColumnDef, RowSelectionState, SortingState} from "@tanstack/react-table";
import React, {useContext, useEffect, useMemo, useState} from "react";
import Table from "../../components/base-table";
import {StpQualifier} from "../../graphql/pod/generated/api";
import {PODService} from "../../services/PODService";
import {OptionValue} from "../../shared/OptionValue";
import SnackbarContext, {SnackbarStatus} from "../../shared/providers/SnackbarContext";
import {useUserSettings} from "../../shared/UserSettingsProvider";
import {destinationCountryOptionFromValue, euCountryOptions, stpCodeOptionFromValue, stpCodeOptions} from "./Constants";
import {downloadPODCorrectionsCSV} from "./PODCorrectionsDownload";
import "./POD.scss";
import {mapToMutationRecord, mapToRow} from "./PODCorrectionsMapper";
import {destinationCountryHasChanged, destinationCountryIsRequired, newRowInError, PODCorrectionsRow, podNumberHasChanged, podNumberIsRequired, rowIsDirty, stpCodeHasChanged, stpCodeIsRequired} from "./PODCorrectionsRow";

export function PODCorrectionsOverview(props: {
    activeTabId: string,
    podService: PODService
}) {

    interface DataState {
        createdCount: number;
        changedCount: number;
        deletedCount: number;
        inErrorCount: number;
    }

    const initialDataState = useMemo(() => ({createdCount: 0, changedCount: 0, deletedCount: 0, inErrorCount: 0}), []);

    const snackbarCtx = useContext(SnackbarContext);
    const {user} = useUserSettings();

    const [searchField, setSearchField] = useState<string>("");
    const [debouncedSearchField, setDebouncedSearchField] = useState<string>("");
    const debounceTimeout = React.useRef<NodeJS.Timeout | null>(null);

    const [loading, setLoading] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [downloading, setDownloading] = useState<boolean>(false);

    const [data, setData] = useState<PODCorrectionsRow[]>([]);
    const [dataState, setDataState] = useState<DataState>(initialDataState);
    const [dataIsDirty, setDataIsDirty] = useState<boolean>(false);
    const [dataHasError, setDataHasError] = useState<boolean>(false);
    const [saveModalOpen, setSaveModalOpen] = React.useState<boolean>(false);
    const [pendingChangesModalOpen, setPendingChangesModalOpen] = React.useState<boolean>(false);
    const [canSave, setCanSave] = React.useState<boolean>(false);
    const [canDownload, setCanDownload] = React.useState<boolean>(false);

    // Table state for base-table
    const [sorting, setSorting] = useState<SortingState>([]);
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

    useEffect(() => {
        if (data && data.length > 0) {
            setDataState({
                createdCount: data.filter(row => !!row.newPodNumber && !row.toBeDeleted && !row.podNumber).length,
                changedCount: data.filter(row =>
                    !row.toBeDeleted &&
                    row.podNumber &&
                    (podNumberHasChanged(row) || stpCodeHasChanged(row) || destinationCountryHasChanged(row))).length,
                deletedCount: data.filter(row => row.toBeDeleted).length,
                inErrorCount: data.filter(row => newRowInError(row)).length,
            });
        } else {
            setDataState(initialDataState);
        }
    }, [data, initialDataState]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setDataIsDirty(dataState.changedCount > 0 || dataState.createdCount > 0 || dataState.deletedCount > 0);
        setDataHasError(dataState.inErrorCount > 0);
    }, [dataState]);

    useEffect(() => {
        setCanSave(dataIsDirty && !dataHasError && !saving && !downloading);
    }, [dataIsDirty, dataHasError, saving, downloading]);

    useEffect(() => {
        setCanDownload(hasData && !saving && !downloading);
    }, [data, saving, downloading]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setData([]);
        if (debouncedSearchField !== "") {
            performSearch(debouncedSearchField);
        }
    }, [debouncedSearchField]); // eslint-disable-line react-hooks/exhaustive-deps

    // Sync row selection with rowIsDirty - mark dirty rows as selected
    useEffect(() => {
        const newSelection: RowSelectionState = {};
        data.forEach((row) => {
            if (rowIsDirty(row)) {
                newSelection[row.id] = true;
            }
        });
        setRowSelection(newSelection);
    }, [data]);

    function searchFieldChangeHandler(event: React.ChangeEvent<HTMLInputElement>) {
        setSearchField(event.target.value);
        if (dataIsDirty) {
            setPendingChangesModalOpen(true);
        } else {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
            debounceTimeout.current = setTimeout(() => setDebouncedSearchField(event.target.value), 300);
        }
    }

    function performSearch(searchField: string) {
        setLoading(true);
        props.podService.findDeliveryPreferentialOriginDocumentsByFilter(searchField)
            .then((results) => {
                // Defensive: always treat results as an array
                if (!Array.isArray(results) || results.length === 0) {
                    snackbarCtx.displayMsg("No data found for the given search", SnackbarStatus.neutral);
                    setData([]);
                } else {
                    if (results.length >= 200) {
                        snackbarCtx.displayMsg("More than 200 records found. Please refine your search.", SnackbarStatus.neutral);
                    }
                    setData(results.map(e => mapToRow(e)));
                }
            })
            .catch((error) => {
                setData([]);
                console.error("Error fetching POD data", error);
                switch (error?.cause?.statusCode) {
                    case 401:
                        snackbarCtx.displayMsg("Your session has expired. Please log in again.", SnackbarStatus.error);
                        break;
                    case 403:
                        snackbarCtx.displayMsg("It seems you are not allowed to execute this function, request access to group App.CCH.Trade.FilingSpecialist", SnackbarStatus.error, 10000);
                        break
                    default:
                        snackbarCtx.displayMsg("An error occurred while fetching POD data, check with your support if the issue persists.", SnackbarStatus.error);
                }
            })
            .finally(() => setLoading(false));
    }

    const hasData = data.length > 0;

    function podNumberChangeHandler(id, value: string) {
        setData(data => data.map(row => row.id === id ? {...row, newPodNumber: value} : row));
    }

    function destinationCountryChangeHandler(id, option: OptionValue) {
        setData(data => data.map(row => row.id === id ? {...row, newDestinationCountry: option.value} : row));
    }

    function stpCodeChangeHandler(id, option: OptionValue) {
        setData(data => data.map(row => row.id === id ? {...row, newStpCode: option.value} : row));
    }

    function resetRowHandler(id) {
        setData(data => data.map(row => row.id === id ? {...row, newDestinationCountry: "", newPodNumber: "", newStpCode: "", toBeDeleted: false} : row));
    }

    function deleteRowHandler(id) {
        setData(data => data.map(row => row.id === id ? {...row, toBeDeleted: true} : row));
    }

    function saveChangedRows() {
        setSaving(true);
        props.podService.mutateDeliveryPodData(data.filter(row => rowIsDirty(row)).map(row => mapToMutationRecord(row, user)))
            .then(() => {
                snackbarCtx.displayMsg("Saved successfully", SnackbarStatus.success);
                setData([]);
                performSearch(searchField);
            })
            .catch((error) => {
                console.error("Error saving POD data", error)
                snackbarCtx.displayMsg("Something went wrong while saving your POD Corrections", SnackbarStatus.error);
            })
            .finally(() => {
                setSaveModalOpen(false)
                setSaving(false);
            });
    }

    const columns = useMemo<ColumnDef<PODCorrectionsRow>[]>(() => [
        {
            header: "Qual?",
            accessorKey: "stpQualifier",
            meta: {
                tooltip: "Does this delivery qualify for an STP?"
            },
            sortingFn: (rowA, rowB) => Number(rowB.original.stpQualifier) - Number(rowA.original.stpQualifier),
            cell: (props) => {
                let content: React.ReactNode | null = null;
                switch (props.getValue() as StpQualifier) {
                    case StpQualifier.Y:
                        content = <Check className="eds-color--green-30" size="m"/>;
                        break;
                    case StpQualifier.N:
                        content = <Close className="eds-color--red-30" size="m"/>;
                        break;
                    case StpQualifier.UNKNOWN:
                        content = <Question className="eds-color--blue-20" size="m"/>;
                        break;
                    default:
                        content = <div/>; // tooltip needs an element
                }
                return (
                    props.getValue() === StpQualifier.Y
                        ? <Tooltip bodySlot={"Master Data: " + props.row.original.stpCodeMasterData}>{content}</Tooltip>
                        : content
                )
            }
        },
        {
            header: "New POD Nbr",
            accessorKey: "newPodNumber",
            cell: (props) => {
                return <TextField
                    id="id"
                    label={""}
                    hideLabel={true}
                    value={props.getValue() as string}
                    onChange={(e) => podNumberChangeHandler(props.row.original.id, e.target.value)}
                    disabled={props.row.original.toBeDeleted}
                    style={{width: "100%", minWidth: "200px"}}
                    hasErrors={podNumberIsRequired(props.row.original)}
                    errorMessage="Required"
                    className="compactField"
                />
            }
        },
        {
            header: "POD Nbr",
            accessorKey: "podNumber",
            cell: (props) => {
                return (props.row.original.toBeDeleted
                    ? <span style={{textDecoration: "line-through"}}>{props.getValue() as string}</span>
                    : props.getValue() as string)
            }
        },
        {
            header: "POD Ctry",
            accessorKey: "newDestinationCountry",
            sortingFn: (rowA, rowB) => {
                const valA = rowA.original.newDestinationCountry || rowA.original.destinationCountry || "";
                const valB = rowB.original.newDestinationCountry || rowB.original.destinationCountry || "";
                return valA.localeCompare(valB);
            },
            cell: (props) => {
                return (
                    <div style={{width: "175px"}}>
                        <Select
                            isDisabled={props.row.original.toBeDeleted}
                            id="destinationCountrySelect"
                            options={euCountryOptions}
                            label=""
                            hideLabel={true}
                            value={
                                props.getValue()
                                    ? destinationCountryOptionFromValue(props.getValue() as string)
                                    : destinationCountryOptionFromValue(props.row.original.destinationCountry)}
                            onChange={(option) =>
                                destinationCountryChangeHandler(props.row.original.id, option as OptionValue)}
                            className="compactSelectField"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            hasErrors={destinationCountryIsRequired(props.row.original)}
                            errorMessage="Required"
                        />
                    </div>
                )
            }
        },
        {
            header: "POD STP",
            accessorKey: "newStpCode",
            sortingFn: (rowA, rowB) => {
                const valA = rowA.original.newStpCode || rowA.original.stpCode || "";
                const valB = rowB.original.newStpCode || rowB.original.stpCode || "";
                return valA.localeCompare(valB);
            },
            cell: (props) => {
                return (
                    <div style={{width: "175px"}}>
                        <Select
                            isDisabled={props.row.original.toBeDeleted}
                            id="stpCodeSelect"
                            options={stpCodeOptions}
                            label=""
                            hideLabel={true}
                            value={props.getValue()
                                ? stpCodeOptionFromValue(props.getValue() as string)
                                : props.row.original.stpCode
                                    ? stpCodeOptionFromValue(props.row.original.stpCode)
                                    : stpCodeOptionFromValue(props.row.original.stpCodeMasterData)}
                            onChange={(option) =>
                                stpCodeChangeHandler(props.row.original.id, option as OptionValue)}
                            className="compactSelectField"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            hasErrors={stpCodeIsRequired(props.row.original)}
                            errorMessage="Required"
                        />
                    </div>
                )
            }

        },
        {
            header: "MCO",
            accessorKey: "materialCountryOfOrigin",
        },
        {
            header: "FCI",
            accessorKey: "factoryCommercialInvoiceNumber",
        },
        {
            header: "Container",
            accessorKey: "transportEquipmentNumber",
        },
        {
            header: "CRPO",
            accessorKey: "purchaseOrderNumber",
        },
        {
            header: "Line",
            accessorKey: "purchaseOrderLineNumber",
        },
        {
            header: "Product Code",
            accessorKey: "productCode",
        },
        {
            header: "Units",
            accessorKey: "quantity",
        },
        {
            header: "",
            accessorKey: "id",
            enableSorting: false,
            cell: (props) => {
                const canDelete = !rowIsDirty(props.row.original) && !!props.row.original.podNumber;
                const canUndo = rowIsDirty(props.row.original);
                return (
                    <div className="eds-flex eds-gap--8">
                        {canDelete ? (
                            <Tooltip bodySlot="Delete POD" placement="top">
                                <IconButton
                                    size="small"
                                    onClick={() => deleteRowHandler(props.getValue() as string)}
                                    icon="Delete"
                                    label="Delete Row"
                                />
                            </Tooltip>
                        ) : (
                            <Tooltip bodySlot={
                                props.row.original.toBeDeleted
                                    ? "Already deleted"
                                    : rowIsDirty(props.row.original)
                                        ? "POD changed: undo first"
                                        : "No POD to delete"}
                                     placement="top">
                                <IconButton size="small" disabled icon="Delete" label="Delete Row"/>
                            </Tooltip>
                        )}
                        {canUndo ? (
                            <Tooltip bodySlot="Undo changes" placement="top">
                                <IconButton
                                    size="small"
                                    onClick={() => resetRowHandler(props.getValue() as string)}
                                    icon="Undo"
                                    label="Undo"
                                />
                            </Tooltip>
                        ) : (
                            <Tooltip bodySlot="Row has no changes" placement="top">
                                <IconButton size="small" disabled icon="Undo" label="Undo Row"/>
                            </Tooltip>
                        )}
                    </div>
                );
            }
        }
    ], [hasData]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div hidden={props.activeTabId !== 'corrections'}>
            <Card className="eds-flex eds-flex--direction-column eds-gap--16 overflowAuto">
                <div className="eds-grid--item-align-end" style={{width: '33vw'}}>
                    <div className="eds-flex eds-flex--align-items-center eds-gap--16">
                        <div className="eds-flex--grow-1">
                            <TextField onChange={searchFieldChangeHandler}
                                       id="searchField"
                                       type="text"
                                       placeholder={"POD, FCI, CRPO or Container"}
                                       beforeSlot={<Search/>}
                                       label=""
                                       value={searchField ?? ""}
                                       hideLabel={true}
                            />
                        </div>
                        <div className="eds-flex eds-gap--8">
                            {canSave ? (
                                <Tooltip
                                    bodySlot={
                                        dataHasError ? (
                                            <span>Fix the error(s) first</span>
                                        ) : (
                                            <>
                                                {dataState.createdCount > 0 && <span>{dataState.createdCount} new&nbsp;</span>}
                                                {dataState.changedCount > 0 && <span>{dataState.changedCount} changed&nbsp;</span>}
                                                {dataState.deletedCount > 0 && <span>{dataState.deletedCount} deleted</span>}
                                                {dataState.createdCount === 0 && dataState.changedCount === 0 && dataState.deletedCount === 0 && <span>No changes</span>}
                                            </>
                                        )
                                    }
                                    placement="top"
                                >
                                    <Button
                                        onClick={() => setSaveModalOpen(true)}
                                        variant="primary"
                                        size={"small"}
                                        style={{outline: "none"}}
                                        disabled={!canSave}
                                    >
                                        Save
                                    </Button>
                                </Tooltip>
                            ) : (
                                <Button variant="primary" size={"small"} style={{outline: "none"}} disabled>Save</Button>
                            )}
                            {canDownload ? (
                                <Tooltip
                                    bodySlot="Download current table"
                                    placement="top"
                                >
                                    <Button
                                        onClick={() => {
                                            setDownloading(true);
                                            downloadPODCorrectionsCSV(data, snackbarCtx).finally(() => setDownloading(false));
                                        }}
                                        variant="primary"
                                        size={"small"}
                                        style={{outline: "none"}}
                                        disabled={!canDownload}
                                    >
                                        Download
                                    </Button>
                                </Tooltip>
                            ) : (
                                <Button variant="primary" size={"small"} style={{outline: "none"}} disabled>Download</Button>
                            )}
                        </div>
                    </div>
                </div>
                {searchField
                    ? <Table
                        columns={columns}
                        data={data}
                        enableSorting={true}
                        enableRowSelection={true}
                        enableMultiRowSelection={true}
                        state={{
                            sorting,
                            rowSelection
                        }}
                        onSortChange={setSorting}
                        onRowSelectionChange={() => {}} // Disable manual selection - selection is automatic based on rowIsDirty
                        getRowId={(row) => row.id}
                        noDataText={loading ? "Loading..." : "No data found"}
                        className="pod-corrections-table"
                    />
                    : <span className="eds-type--title-5" style={{textAlign: "center"}}>
                        Please use search to display data
                    </span>
                }
            </Card>
            <Modal
                onDismiss={() => setSaveModalOpen(!saveModalOpen)}
                isOpen={saveModalOpen}
                headerSlot="About to save"
                footerSlot={
                    <ButtonGroup>
                        <Button
                            onClick={saveChangedRows}
                            size="small"
                            beforeSlot={saving ? <span><Spinner/></span> : undefined}
                        >
                            Save
                        </Button>
                        <Button
                            onClick={() => setSaveModalOpen(!saveModalOpen)}
                            size="small"
                            variant="secondary"
                        >
                            Cancel
                        </Button>
                    </ButtonGroup>
                }
            >
                <span>
                    <ul>
                        {dataState.createdCount > 0 && <li>{dataState.createdCount} new</li>}
                        {dataState.changedCount > 0 && <li>{dataState.changedCount} change(s)</li>}
                        {dataState.deletedCount > 0 && <li>{dataState.deletedCount} delete(s)</li>}
                    </ul>
                </span>
            </Modal>
            <Modal
                onDismiss={() => setPendingChangesModalOpen(!pendingChangesModalOpen)}
                isOpen={pendingChangesModalOpen}
                headerSlot="About to loose all changes!"
                footerSlot={
                    <ButtonGroup>
                        <Button
                            onClick={() => {
                                setPendingChangesModalOpen(!pendingChangesModalOpen);
                                setDebouncedSearchField(searchField)    // trigger search
                            }}
                            size="small"
                        >
                            Search anyway
                        </Button>
                        <Button
                            onClick={() => {
                                setSearchField(debouncedSearchField);   // reset search field to last searched value
                                setTimeout(() => {               // puts focus back to search field and cursor after value
                                    const input = document.getElementById("searchField") as HTMLInputElement | null;
                                    if (input) {
                                        input.focus();
                                        input.setSelectionRange(input.value.length, input.value.length);
                                    }
                                }, 0);
                                setPendingChangesModalOpen(!pendingChangesModalOpen);
                            }}
                            size="small"
                            variant="secondary"
                        >
                            Cancel
                        </Button>
                    </ButtonGroup>
                }
            >
                <span>
                    <ul>
                        {dataState.createdCount > 0 && <li>{dataState.createdCount} new</li>}
                        {dataState.changedCount > 0 && <li>{dataState.changedCount} change(s)</li>}
                        {dataState.deletedCount > 0 && <li>{dataState.deletedCount} delete(s)</li>}
                    </ul>
                </span>
            </Modal>
        </div>
    );
}
